import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Download, ExternalLink, MessageCircle, RefreshCw } from "lucide-react";
import { CopyableField, Toggle, confirmDelete, showErrorAlert } from "../../../../components/ui/index.js";
import { catalogPublicUrl, shareCatalogText, whatsappUrl } from "../../../../components/catalog/catalogUtils.js";

/** `web.com/catalogo/4nTq…kbP`: legible en una columna estrecha. */
const shortUrl = (url, token) => {
    const host = url.replace(/^https?:\/\//, "").split("/")[0];
    const shortToken = token.length > 10 ? `${token.slice(0, 5)}…${token.slice(-3)}` : token;
    return `${host}/catalogo/${shortToken}`;
};

/**
 * Compartir el catálogo de un proyecto (C3D-38): el enlace (activo o no), el
 * envío por WhatsApp al teléfono del lead o cliente, el PDF y las visitas.
 */
export default function SharePanel({ project, customerPhone }) {
    const [link, setLink] = useState(null);
    const [error, setError] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setError(false);
        axios.get(`/projects/${project.id}/link/`)
            .then(({ data }) => setLink(data))
            .catch(() => setError(true));
    }, [project.id]);
    useEffect(load, [load]);

    const setActive = async (isActive) => {
        setBusy(true);
        try {
            const { data } = await axios.patch(`/projects/${project.id}/link/`, { is_active: isActive });
            setLink(data);
        } catch {
            showErrorAlert({ text: "No se ha podido cambiar el enlace." });
        } finally {
            setBusy(false);
        }
    };

    const regenerate = async () => {
        const confirmed = await confirmDelete({
            title: "Cambiar el enlace",
            text: "El enlace que ya hayas mandado dejará de abrir. Tendrás que mandar el nuevo.",
            confirmButtonText: "Cambiar enlace",
        });
        if (!confirmed) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`/projects/${project.id}/link/regenerate/`);
            setLink(data);
        } catch {
            showErrorAlert({ text: "No se ha podido cambiar el enlace." });
        } finally {
            setBusy(false);
        }
    };

    const downloadPdf = async () => {
        setBusy(true);
        try {
            const { data } = await axios.get(`/projects/${project.id}/pdf/`, {
                params: { origin: window.location.origin },
                responseType: "blob",
            });
            const url = URL.createObjectURL(data);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `catalogo-${project.name}.pdf`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch {
            showErrorAlert({ text: "No se ha podido generar el PDF." });
        } finally {
            setBusy(false);
        }
    };

    if (error) {
        return (
            <section className="rounded-xl border border-base-content/10 p-4 flex flex-col gap-2">
                <h2 className="font-semibold">Compartir</h2>
                <p role="alert" className="text-sm text-error">No se ha podido cargar el enlace.</p>
                <button type="button" className="btn btn-sm self-start" onClick={load}>Reintentar</button>
            </section>
        );
    }
    if (!link) {
        return <div className="skeleton h-56 rounded-xl" aria-busy="true" />;
    }

    const url = catalogPublicUrl(link.token);
    const message = shareCatalogText(project.customer_name, project.name, url);

    return (
        <section className="rounded-xl border border-base-content/10 bg-base-100 p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold">Compartir catálogo</h2>
                <Toggle checked={link.is_active} onChange={setActive} label={link.is_active ? "Activo" : "Desactivado"} disabled={busy} />
            </div>

            {/* El token completo no cabe en la columna: se enseña acortado y se copia entero. */}
            <CopyableField
                label="Enlace"
                value={link.is_active ? url : ""}
                display={shortUrl(url, link.token)}
                emptyText="Desactivado: no abre"
                mono={false}
                helpText={`${link.view_count} ${link.view_count === 1 ? "visita" : "visitas"}${link.last_viewed_at ? `, la última el ${new Date(link.last_viewed_at).toLocaleDateString("es-ES")}` : ""}`}
            />

            <div className="flex flex-col gap-2">
                <a
                    href={whatsappUrl(customerPhone, message)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn btn-primary btn-sm w-full gap-2 whitespace-nowrap ${link.is_active ? "" : "btn-disabled"}`}
                    aria-disabled={!link.is_active}
                >
                    <MessageCircle className="w-4 h-4 shrink-0" aria-hidden="true" /> Enviar por WhatsApp
                </a>
                <div className="grid grid-cols-2 gap-2">
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`btn btn-outline btn-sm gap-1.5 whitespace-nowrap ${link.is_active ? "" : "btn-disabled"}`}
                        aria-disabled={!link.is_active}
                        title="Abrir el catálogo como lo ve el cliente"
                    >
                        <ExternalLink className="w-4 h-4 shrink-0" aria-hidden="true" /> Ver catálogo
                    </a>
                    <button
                        type="button"
                        className="btn btn-outline btn-sm gap-1.5 whitespace-nowrap"
                        onClick={downloadPdf}
                        disabled={busy}
                        title="Descargar el catálogo en PDF"
                    >
                        <Download className="w-4 h-4 shrink-0" aria-hidden="true" /> PDF
                    </button>
                </div>
            </div>

            {!customerPhone && (
                <p className="text-xs text-base-content/60">Sin teléfono del lead o cliente: WhatsApp te dejará elegir a quién mandarlo.</p>
            )}

            <div className="flex items-start justify-between gap-3 border-t border-base-content/10 pt-3">
                <p className="text-xs text-base-content/60">Si cambias el enlace, el que ya mandaste deja de abrir.</p>
                <button type="button" className="btn btn-ghost btn-xs gap-1 whitespace-nowrap shrink-0" onClick={regenerate} disabled={busy}>
                    <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Cambiar enlace
                </button>
            </div>
        </section>
    );
}

SharePanel.propTypes = {
    project: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string,
        customer_name: PropTypes.string,
    }).isRequired,
    customerPhone: PropTypes.string,
};
